import { useDispatch, useSelector } from 'react-redux';
import RestaurantInventoryService from '../../services/restaurant/RestaurantInventoryService';
import RestaurantRawMaterialCategoryService from '../../services/restaurant/RestaurantRawMaterialCategoryService';
import RestaurantRawMaterialService from '../../services/restaurant/RestaurantRawMaterialService';
import { AppDispatch, RootState } from '../../store/store';
import {
  closeRestaurantInventoryModal,
  openRestaurantInventoryCreate,
  openRestaurantInventoryEdit,
  setRestaurantInventory,
  setRestaurantInventoryLoading,
  setRestaurantInventoryOptions,
  setRestaurantInventorySaving,
  updateRestaurantInventoryField,
} from '../../store/slices/restaurant/restaurantInventorySlice';
import {
  RestaurantInventoryDTO,
  RestaurantInventoryFilters,
  RestaurantInventoryForm,
} from '../../types/restaurant/RestaurantInventoryTypes';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const useRestaurantInventory = (branchId: number | '') => {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((root: RootState) => root.restaurantInventory);
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();

  const load = async (filters: RestaurantInventoryFilters = {}) => {
    if (!branchId) return;
    dispatch(setRestaurantInventoryLoading(true));
    try {
      const [inventory, rawMaterials, categories] = await Promise.all([
        RestaurantInventoryService.list(branchId, filters),
        RestaurantRawMaterialService.listFiltered(branchId, {
          page: 0, size: 100, sort: ['name,asc'],
          filters: { search: null, categoryId: null, unitId: null, supplierId: null, active: true },
        }),
        RestaurantRawMaterialCategoryService.listFiltered(branchId, {
          page: 0, size: 100, sort: ['name,asc'],
          filters: { search: null, active: true },
        }),
      ]);
      dispatch(setRestaurantInventory(inventory));
      dispatch(setRestaurantInventoryOptions({
        rawMaterials: rawMaterials.content,
        categories: categories.content,
        existingRawMaterialIds: inventory.content.map((item) => item.rawMaterialId),
      }));
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setRestaurantInventoryLoading(false));
    }
  };

  const edit = async (item: RestaurantInventoryDTO) => {
    try {
      const response = await RestaurantInventoryService.get(item.id);
      dispatch(openRestaurantInventoryEdit(response.data));
    } catch (error) {
      handle(error);
    }
  };

  const save = async (filters: RestaurantInventoryFilters = {}) => {
    if (!branchId || state.saving) return;
    dispatch(setRestaurantInventorySaving(true));
    try {
      const response = state.form.id
        ? await RestaurantInventoryService.update(state.form.id, state.form)
        : await RestaurantInventoryService.create(branchId, state.form);
      show(
        response.type || 'success',
        response.title || 'Inventario guardado',
        response.message || 'El registro de inventario se guardó correctamente.',
      );
      dispatch(closeRestaurantInventoryModal());
      await load(filters);
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setRestaurantInventorySaving(false));
    }
  };

  const toggleActive = (item: RestaurantInventoryDTO, filters: RestaurantInventoryFilters = {}) => {
    const execute = async () => {
      try {
        const response = await RestaurantInventoryService.updateActive(item.id, !item.active);
        show(
          response.type || 'success',
          response.title || 'Inventario actualizado',
          response.message || 'El estado se actualizó correctamente.',
        );
        await load(filters);
      } catch (error) {
        handle(error);
      }
    };

    if (!item.active) return execute();
    confirm({
      title: 'Desactivar registro de inventario',
      message: `¿Desea desactivar el inventario de “${item.rawMaterialName}”?`,
      icon: 'warning',
      confirmText: 'Sí, desactivar',
      cancelText: 'Cancelar',
      onConfirm: execute,
    });
  };

  const updateField = <K extends keyof RestaurantInventoryForm>(
    field: K,
    value: RestaurantInventoryForm[K],
  ) => dispatch(updateRestaurantInventoryField({ field, value }));

  return {
    ...state,
    load,
    edit,
    save,
    toggleActive,
    updateField,
    openCreate: () => dispatch(openRestaurantInventoryCreate()),
    closeModal: () => dispatch(closeRestaurantInventoryModal()),
  };
};

export default useRestaurantInventory;
